import { SectionTitle } from 'components/Section';
import styles from './contactSection.scss';
import connect from 'components/Shared/connect';
import { PageState } from 'models/PageState';
import { StoresModel } from 'models/StoresModel';
import { Store } from 'components/StoreInfo/themes/BGH-RD3/StoresInfo';
import { Button } from 'components/Button';
import { rm, hasElements, NO_ELEMENT } from 'utils';
import cn from 'classnames';
import { ContactBlock } from 'models/ContactSection';
import { IconCheckmark } from 'components/Shared/Icon';
import { IconPhone } from 'components/Shared/Icon/themes/BGH-RD3/iconPhone';
import { IconMail } from 'components/Shared/Icon/themes/BGH-RD3/iconMail';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { isArredoChannel } from 'components/Shared/channel';
import { useDispatch } from 'react-redux';
import { GtmFooterNavClick } from 'store/googleTagManager/actions';
import { BlockWrapper, Wrapper } from './footer.styles';

interface ContactSection {
  onNavClick: (url: string) => void;
}
interface NewContactSection extends ContactSection {
  contactBlocks: ContactBlock[];
}

interface ContactProps extends NewContactSection {
  contactUrl: string;
  message: string;
  critical: boolean;
  showOfferBtn: boolean;
  isNewStyle: boolean;
}

interface ContactSectionProps extends Pick<PageState, 'stores'>, ContactProps {
  shouldUseHelpCenter: boolean;
}

interface StoresProps extends ContactSection {
  storesModel: StoresModel;
  isNewStyle: boolean;
}

const Stores = ({ storesModel, onNavClick, isNewStyle }: StoresProps) => {
  const { stores, header } = storesModel;

  if (!hasElements(stores)) {
    return NO_ELEMENT;
  }

  return (
    <div className={cn(styles.stores, { [styles.newStores]: isNewStyle })}>
      <SectionTitle title={header} className={isNewStyle ? styles.newheading : styles.heading} />
      <div className={cn(styles.storeList, { [styles.newStoresList]: isNewStyle })}>
        {stores.map((store, i) => (
          <div key={i}>
            <Store
              className={isNewStyle ? styles.newStoreLink : styles.storeLink}
              onClick={() => onNavClick(store.url)}
              {...store}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const btnLink = (contactBlock: ContactBlock) => {
  let res = `tel:${contactBlock.phoneNumber}`;
  if ('link' in contactBlock) {
    res = contactBlock.link;
  }
  return res;
};

const btnContent = (contactBlock: ContactBlock) => {
  let res = (
    <>
      <IconPhone className={styles.phoneIcon} />
      {contactBlock.phoneNumber}
    </>
  );
  if ('link' in contactBlock) {
    res = (
      <>
        <span className={styles.contactFormCaption}>{contactBlock.linkText}</span>
        <IconArrow size="small" />
      </>
    );
  }
  return res;
};

const NewContact = ({ contactBlocks, onNavClick }: NewContactSection) => (
  <Wrapper className={styles.newContact}>
    {contactBlocks.map(contactBlock => (
      <BlockWrapper key={contactBlock.title}>
        <SectionTitle className={styles.heading} title={contactBlock.title} />
        <p>{contactBlock.uspTitleFooter}</p>
        <ul>
          {contactBlock.usps.map(usp => (
            <li key={usp}>
              <h3>
                <IconCheckmark className={styles.checkmarkIcon} size="small" />
                &nbsp;&nbsp;
                {usp}
              </h3>
            </li>
          ))}
        </ul>

        <Button
          onClick={() => onNavClick(rm('footer_customer_service_link'))}
          href={btnLink(contactBlock)}
          className={cn(styles.contactButton)}
        >
          {btnContent(contactBlock)}
        </Button>
      </BlockWrapper>
    ))}
  </Wrapper>
);

const Contact = ({
  contactBlocks,
  contactUrl,
  message,
  critical,
  showOfferBtn,
  onNavClick,
  isNewStyle
}: ContactProps) => (
  <div className={styles.contact}>
    <SectionTitle title={rm('contactinfo_contactus')} className={styles.heading} />
    {message && (
      <div className={cn(styles.contactMessage, critical && styles.critical)}>{message}</div>
    )}
    {contactBlocks.map(contactBlock => (
      <div key={contactBlock.title} className={styles.contactBlock}>
        <div>
          <p className={isNewStyle ? styles.newContactTitle : styles.contactTitle}>
            <span>{contactBlock.title}</span>
            <span>&nbsp;-&nbsp;</span>
            <span>{contactBlock.subTitle}</span>
          </p>
          <ul className={styles.contactUsps}>
            {contactBlock.usps.map(usp => (
              <li key={usp}>
                <IconCheckmark className={styles.checkmarkIcon} size="small" />
                &nbsp;
                {usp}
              </li>
            ))}
          </ul>
        </div>
        <Button href={`tel:${contactBlock.phoneNumber}`} className={styles.contactButton}>
          <IconPhone className={styles.phoneIcon} />
          {contactBlock.phoneNumber}
        </Button>
      </div>
    ))}
    <Button
      onClick={() => onNavClick(contactUrl)}
      href={contactUrl}
      className={cn(styles.contactButton, styles.contactBlock)}
    >
      <IconMail />
      <span className={styles.contactFormCaption}>{rm('contact_form')}</span>
      <IconArrow size="small" />
    </Button>
    {showOfferBtn && (
      <Button
        onClick={() => onNavClick(rm('footer_offer_link'))}
        href={rm('footer_offer_link')}
        className={cn(styles.contactButton, styles.contactBlock, styles.offerButton)}
      >
        <span className={styles.contactFormCaption}>{rm('footer_offer_button')}</span>
        <IconArrow size="small" />
      </Button>
    )}
  </div>
);

const _ContactSection = ({
  stores,
  contactBlocks,
  contactUrl,
  message,
  critical,
  showOfferBtn,
  shouldUseHelpCenter
}: ContactSectionProps) => {
  const reduxDispatch = useDispatch();

  const handleOnClick = (url: string) => {
    reduxDispatch(GtmFooterNavClick(url));
  };

  return (
    <div className={shouldUseHelpCenter ? cn(styles.newContactSection) : cn(styles.contactSection)}>
      {shouldUseHelpCenter ? (
        <NewContact onNavClick={handleOnClick} contactBlocks={contactBlocks} />
      ) : (
        <Contact
          onNavClick={handleOnClick}
          contactBlocks={contactBlocks}
          contactUrl={contactUrl}
          message={message}
          critical={critical}
          showOfferBtn={showOfferBtn}
          isNewStyle={shouldUseHelpCenter}
        />
      )}
      <Stores storesModel={stores} onNavClick={handleOnClick} isNewStyle={shouldUseHelpCenter} />
    </div>
  );
};

export const ContactSection = connect<ContactSectionProps>(state => ({
  stores: state.stores,
  message: state.header.contactSection ? state.header.contactSection.message : '',
  critical: state.header.contactSection ? state.header.contactSection.isCritical : false,
  contactBlocks: state.header.contactSection ? state.header.contactSection.blocks : [],
  contactUrl: state.header.contactSection ? state.header.contactSection.contactUrl : '',
  showOfferBtn: isArredoChannel(state.appShell.channel),
  shouldUseHelpCenter: state.appShell.shouldUseHelpCenter
}))(_ContactSection);
